import { SetStateAction, MouseEvent, Dispatch } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { IEventInfo } from "./EventCalendar";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import GroupIcon from "@mui/icons-material/Group";
import axios from "axios";
import { useEffect, useState } from "react";

interface IProps {
  open: boolean;
  handleClose: Dispatch<SetStateAction<void>>;
  onDeleteEvent: (e: MouseEvent<HTMLButtonElement>) => void;
  currentEvent: IEventInfo | null;
}

const EventInfoModal = ({
  open,
  handleClose,
  onDeleteEvent,
  currentEvent,
}: IProps) => {
  const onClose = () => {
    handleClose();
  };
  const authUser = useAuthUser<any>();

  const [attendees, setAttendees] = useState<string[]>([]);
  const [isLoadingAttendees, setIsLoadingAttendees] = useState(false);
  const [attendeeError, setAttendeeError] = useState<string | null>(null);

  // Temporary getAuthToken placeholder - MOVE THIS TO A SHARED UTILITY
  const getAuthToken = () => {
    const cookies = document.cookie.split(";");
    let token = null;
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_auth") {
        token = decodeURIComponent(value);
        break;
      }
    }
    return token;
  };

  useEffect(() => {
    if (open && currentEvent?._id) {
      const fetchAttendees = async () => {
        setIsLoadingAttendees(true);
        setAttendeeError(null);
        setAttendees([]);
        try {
          const token = getAuthToken();
          if (!token) {
            setAttendeeError("Authentication token not found.");
            setIsLoadingAttendees(false);
            return;
          }
          const response = await axios.get(
            // Ensure process.env.REACT_APP_API_URL is correctly configured
            `${
              process.env.REACT_APP_API_URL || "http://localhost:5000"
            }/api/get-event-attendees?eventId=${currentEvent._id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.data && Array.isArray(response.data.attendees)) {
            setAttendees(response.data.attendees);
          } else {
            setAttendees([]);
            // setAttendeeError("Attendees data is not in expected format."); // Optional: more specific error
          }
        } catch (err: any) {
          console.error("Error fetching attendees:", err);
          setAttendeeError(
            err.response?.data?.error || "Failed to load attendees."
          );
          setAttendees([]);
        } finally {
          setIsLoadingAttendees(false);
        }
      };
      fetchAttendees();
    } else {
      setAttendees([]);
      setIsLoadingAttendees(false);
      setAttendeeError(null);
    }
  }, [open, currentEvent]);

  var currentdate = new Date();
  var diff = 0;
  if (currentEvent?.start !== undefined) {
    diff = currentEvent.start.getTime() - currentdate.getTime();
  }

  var uid = -1;
  var utype = -1;
  var tid = -1;
  if (authUser !== null) {
    uid = authUser.uid;
    utype = authUser.typeId;
    tid = authUser.companyId;

    console.log(uid);
    console.log(currentEvent?.userId);
  }

  // Determine the error message based on the conditions
  let errorMessage = "";

  if (authUser === null) {
    errorMessage = "Please log in to delete the event.";
  } else if (diff < 0) {
    errorMessage =
      "This event is in the past. This action will refund the users credits.";
  } else if (uid !== currentEvent?.userId) {
    errorMessage =
      "This event is not created by you. This action will refund the users credits.";
  } else if (utype < 3) {
    errorMessage = "You need the appropriate user type to delete events.";
  }

  console.log(errorMessage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InfoOutlinedIcon />
        Booking Info
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          spacing={2}
          sx={{ mt: 1 }}
        >
          <Box
            hidden={
              (uid !== currentEvent?.userId ||
                tid === currentEvent?.companyId) &&
              utype < 3
            }
          >
            <Stack spacing={1.5}>
              {currentEvent?.description && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EventNoteIcon color='action' />
                  <Typography sx={{ fontWeight: "medium" }}>
                    {currentEvent.description}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MailOutlineIcon color='action' />
                <Typography>Booked By: {currentEvent?.email}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BusinessIcon color='action' />
                <Typography>Company: {currentEvent?.companyName}</Typography>
              </Box>
            </Stack>
          </Box>

          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon color='action' />
              <Typography>
                Start:{" "}
                {currentEvent?.start?.toLocaleDateString(undefined, {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}{" "}
                {currentEvent?.start?.toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon color='action' />
              <Typography>
                End:{" "}
                {currentEvent?.end?.toLocaleDateString(undefined, {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}{" "}
                {currentEvent?.end?.toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </Typography>
            </Box>
          </Stack>

          {/* Attendees Section Placeholder */}
          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon color='action' />
              <Typography
                variant='subtitle1'
                sx={{ fontWeight: "medium" }}
              >
                Attendees:
              </Typography>
            </Box>
            {isLoadingAttendees ? (
              <Typography
                variant='body2'
                sx={{ pl: 3.5 }}
              >
                Loading attendees...
              </Typography>
            ) : attendeeError ? (
              <Alert
                severity='error'
                sx={{ ml: 3.5, mr: 1 }}
              >
                {attendeeError}
              </Alert>
            ) : attendees.length > 0 ? (
              <Box sx={{ pl: 3.5 }}>
                {attendees.map((attendee, index) => (
                  <Typography
                    key={index}
                    variant='body2'
                  >
                    {attendee}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography
                variant='body2'
                sx={{ pl: 3.5 }}
              >
                No attendees listed for this event or you may not have
                permission to view them.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          startIcon={<CancelOutlinedIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={onDeleteEvent}
          variant='contained'
          color='error'
          startIcon={<DeleteOutlineIcon />}
          disabled={
            (authUser === null || diff < 0 || uid !== currentEvent?.userId) &&
            utype < 3
          }
          aria-describedby={errorMessage ? "delete-event-error" : undefined}
        >
          Delete Event
        </Button>
      </DialogActions>
      {utype >= 3 && errorMessage !== "" && (
        <Alert
          variant='outlined'
          severity='warning'
          sx={{ marginBottom: 1, margin: 1 }}
        >
          {errorMessage}
        </Alert>
      )}
    </Dialog>
  );
};

export default EventInfoModal;
